import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    CubeIcon,
    CheckIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    ClipboardDocumentListIcon,
    MagnifyingGlassIcon,
    PlusIcon,
    Square2StackIcon,
    TrashIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { AddDrawerSelect } from "../productCatalog/AddDrawerSelect";
import { ProductCatalogSelectFilter } from "../productCatalog/ProductCatalogSelectFilter";
import { LoaderSpinner } from "../LoaderSpinner";
import { ConfrimModal } from "../ConfrimModal";
import Drawer from "../Drawer";
import { DatePickerInput } from "../DatePickerInput";
import { useDebounce } from "../../helpers/useDebounce";
import { ProductGroupLoadingKeys, getSuppliers } from "../../slices/productGroupSlice";
import {
    addBomPackaging,
    BomUiLoadingKeys,
    clearBomFilters,
    deleteBomPackaging,
    deletePackagingBom,
    fetchPackagingBomUi,
    fetchProductBomUi,
    selectBomUi,
    savePackagingBom,
    savePackagingInstructions,
    searchBomProductGroups,
    setBomFilter,
    setBomSettingsOpen,
    setBomRowSelection,
    toggleBomExpandedRow,
    toggleBomRowSelection,
    updateBomPackaging,
} from "../../slices/bomUiSlice";

const inputSm =
    "h-8 w-full rounded-md border border-gray-200 bg-white px-2.5 text-sm text-gray-900 outline-none transition focus:border-gray-400";
const labelSm = "mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400";
const MIN_EFFECTIVE_DATE = "2000-01-01";

const amberWhenFilterActive = (isActive) =>
    isActive ? "!border-amber-400 !text-amber-700 !bg-amber-50 focus:!ring-amber-400" : "";

const PACKAGING_DRAWER_STACK = {
    supplier: (rowIndex) => `packaging-vnd-${rowIndex}`,
    instructionPreset: "packaging-instruction-preset",
    boxPreset: "packaging-box-preset",
};

function normalizePackagingComponentName(value) {
    return String(value || "").trim().toLowerCase();
}

/** Qty × component lines and total units for box preset dropdown helper text. */
function formatBoxPresetPackagingSummary(preset) {
    const comps = preset?.components;
    if (!Array.isArray(comps) || comps.length === 0) return "No packaging lines";
    const named = comps.filter((c) => String(c?.component || "").trim());
    const totalQty = named.reduce((s, c) => s + Number(c?.qty ?? 0), 0);
    const parts = named.slice(0, 5).map((c) => {
        const q = Number(c.qty ?? 1);
        return `${q}× ${String(c.component).trim()}`;
    });
    const head = parts.join(" · ");
    const tail = named.length > 5 ? ` · +${named.length - 5} more` : "";
    return `${head || "—"}${tail} · ${Number.isFinite(totalQty) ? totalQty : 0} units`;
}

function newComponentRow() {
    return {
        id: `packaging-component-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        componentName: "",
        description: "",
        supplier: "",
        vendorId: "",
        boxPresetId: "",
        qty: 1,
        unitCost: "",
    };
}

function defaultPackagingComponentRows() {
    const rid = () => `packaging-component-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    return [
        { id: rid(), componentName: "Box", description: "", supplier: "", vendorId: "", boxPresetId: "", qty: 1, unitCost: 0.65 },
        { id: rid(), componentName: "Insert", description: "", supplier: "", vendorId: "", boxPresetId: "", qty: 1, unitCost: 0.12 },
        { id: rid(), componentName: "Sleeve", description: "", supplier: "", vendorId: "", boxPresetId: "", qty: 1, unitCost: 0.3 },
    ];
}

function mapBoxPresetLine(c) {
    const qty = Number(c.qty ?? 1);
    const unit = Number(c.unitCost ?? c.cost ?? 0);
    return {
        id: `packaging-component-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        componentName: String(c.component ?? c.componentName ?? "").trim(),
        description: String(c.description ?? "").trim(),
        supplier: String(c.supplier ?? "").trim(),
        vendorId: c.vendorId != null ? String(c.vendorId) : c.vendor_id != null ? String(c.vendor_id) : "",
        boxPresetId:
            c.boxPresetId != null && c.boxPresetId !== ""
                ? String(c.boxPresetId)
                : c.box_preset_id != null && c.box_preset_id !== ""
                  ? String(c.box_preset_id)
                  : "",
        qty,
        unitCost: unit,
    };
}

function PackagingBomDrawer({
    isOpen,
    onClose,
    initialValue,
    packagingList,
    instructionPresets,
    boxPresets,
    onCommit,
    vendorOptions,
    vendorSelectLoading,
}) {
    const dispatch = useDispatch();
    const isEdit = Boolean(initialValue);
    const productInputRef = useRef(null);

    const [groupId, setGroupId] = useState(null);
    const [productName, setProductName] = useState("");
    const [productSearch, setProductSearch] = useState("");
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const [groupSearchLoading, setGroupSearchLoading] = useState(false);
    const [groupResults, setGroupResults] = useState([]);
    const [saving, setSaving] = useState(false);
    const [openDrawerSelectKey, setOpenDrawerSelectKey] = useState(null);
    const wasDrawerOpenRef = useRef(false);
    const debouncedProductSearch = useDebounce(productSearch, 350);

    const [effectiveDate, setEffectiveDate] = useState(() => new Date().toISOString().split("T")[0]);
    const [selectedInstructionPresetId, setSelectedInstructionPresetId] = useState("");
    const [selectedBoxPresetId, setSelectedBoxPresetId] = useState("");
    const [instructionSteps, setInstructionSteps] = useState([]);
    const [components, setComponents] = useState([]);

    const selectedPreset = boxPresets.find((p) => String(p.id) === String(selectedBoxPresetId));

    const instructionPresetSelectOptions = useMemo(
        () =>
            (instructionPresets || []).map((p) => ({
                value: String(p.id),
                label: `${p.name || p.id} — ${Array.isArray(p.steps) ? p.steps.length : 0} step${
                    Array.isArray(p.steps) && p.steps.length !== 1 ? "s" : ""
                }`,
            })),
        [instructionPresets]
    );

    const boxPresetSelectOptions = useMemo(
        () =>
            (boxPresets || []).map((p) => ({
                value: String(p.id),
                label: `${p.name || p.id}${
                    p.totalCost != null ? ` — $${Number(p.totalCost).toFixed(2)}` : ""
                }`,
            })),
        [boxPresets]
    );

    const reservedGroupIds = useMemo(() => {
        const set = new Set();
        for (const p of packagingList || []) {
            if (p?.groupId != null && p.groupId !== "") {
                set.add(String(p.groupId));
            }
        }
        return set;
    }, [packagingList]);

    const visibleGroupResults = useMemo(
        () =>
            (groupResults || []).filter((row) => {
                const id = row?.id;
                if (id == null) return false;
                return !reservedGroupIds.has(String(id));
            }),
        [groupResults, reservedGroupIds]
    );

    const allSearchResultsTaken = useMemo(
        () =>
            Boolean(
                productSearch.trim() &&
                    !groupSearchLoading &&
                    (groupResults || []).length > 0 &&
                    visibleGroupResults.length === 0
            ),
        [groupResults, groupSearchLoading, productSearch, visibleGroupResults.length]
    );

    useEffect(() => {
        if (!isOpen) {
            setGroupResults([]);
            return undefined;
        }
        if (!debouncedProductSearch.trim()) {
            setGroupResults([]);
            setGroupSearchLoading(false);
            return undefined;
        }
        let cancelled = false;
        setGroupSearchLoading(true);
        dispatch(searchBomProductGroups({ search: debouncedProductSearch.trim(), limit: 12 }))
            .unwrap()
            .then((data) => {
                if (!cancelled) setGroupResults(data?.items || []);
            })
            .catch(() => {
                if (!cancelled) setGroupResults([]);
            })
            .finally(() => {
                if (!cancelled) setGroupSearchLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [debouncedProductSearch, dispatch, isOpen]);

    useEffect(() => {
        if (!isOpen || !isEdit || !initialValue?.productName || initialValue.groupId != null) {
            return undefined;
        }
        let cancelled = false;
        dispatch(searchBomProductGroups({ search: String(initialValue.productName).trim(), limit: 20 }))
            .unwrap()
            .then((data) => {
                if (cancelled) return;
                const items = data?.items || [];
                const exact = items.find(
                    (g) => String(g.name || "").trim().toLowerCase() === String(initialValue.productName).trim().toLowerCase()
                );
                if (exact?.id != null) {
                    setGroupId(exact.id);
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [dispatch, isOpen, isEdit, initialValue]);

    // Only reset form when the drawer opens — not when `initialValue` / list data refreshes while editing
    // (otherwise fetchPackagingBomUi or parent re-renders overwrite in-progress edits with stale preset/server rows).
    useEffect(() => {
        if (!isOpen) {
            wasDrawerOpenRef.current = false;
            setOpenDrawerSelectKey(null);
            return;
        }
        const drawerJustOpened = !wasDrawerOpenRef.current;
        wasDrawerOpenRef.current = true;
        if (!drawerJustOpened) {
            return;
        }
        if (isEdit && initialValue) {
            if (initialValue.groupId != null) {
                setGroupId(initialValue.groupId);
            }
            setProductName(initialValue.productName || "");
            setProductSearch(initialValue.productName || "");
            setEffectiveDate(initialValue.effectiveDate || new Date().toISOString().split("T")[0]);
            setSelectedInstructionPresetId("");
            setSelectedBoxPresetId("");
            setInstructionSteps(Array.isArray(initialValue.instructions) ? [...initialValue.instructions] : []);
            setComponents(
                (initialValue.components || []).map((c) => ({
                    id: c.id || `pc-${Date.now()}-${Math.random()}`,
                    componentName: c.componentName ?? "",
                    description: c.description ?? "",
                    supplier: c.supplier ?? "",
                    vendorId: c.vendorId != null ? String(c.vendorId) : c.vendor_id != null ? String(c.vendor_id) : "",
                    boxPresetId:
                        c.boxPresetId != null && c.boxPresetId !== ""
                            ? String(c.boxPresetId)
                            : c.box_preset_id != null && c.box_preset_id !== ""
                              ? String(c.box_preset_id)
                              : "",
                    qty: c.qty ?? 1,
                    unitCost: c.unitCost ?? "",
                }))
            );
            return;
        }
        setGroupId(null);
        setProductName("");
        setProductSearch("");
        setEffectiveDate(new Date().toISOString().split("T")[0]);
        setSelectedInstructionPresetId("");
        setSelectedBoxPresetId("");
        setInstructionSteps([]);
        setComponents(defaultPackagingComponentRows());
    }, [isOpen, initialValue, isEdit]);

    const selectProductGroup = (item) => {
        const id = item?.id;
        const name = String(item?.name || "").trim();
        if (id == null || !name) return;
        setGroupId(id);
        setProductName(name);
        setProductSearch(name);
        setShowProductDropdown(false);
        const existing = packagingList.find(
            (p) => (p.groupId != null && p.groupId === id) ||
                p.productName.trim().toLowerCase() === name.toLowerCase()
        );
        if (existing) {
            setComponents(
                (existing.components || []).map((c) => ({
                    id: c.id || `pc-${Date.now()}-${Math.random()}`,
                    componentName: c.componentName ?? "",
                    description: c.description ?? "",
                    supplier: c.supplier ?? "",
                    vendorId: c.vendorId != null ? String(c.vendorId) : c.vendor_id != null ? String(c.vendor_id) : "",
                    boxPresetId:
                        c.boxPresetId != null && c.boxPresetId !== ""
                            ? String(c.boxPresetId)
                            : c.box_preset_id != null && c.box_preset_id !== ""
                              ? String(c.box_preset_id)
                              : "",
                    qty: c.qty ?? 1,
                    unitCost: c.unitCost ?? "",
                }))
            );
            setEffectiveDate(existing.effectiveDate || new Date().toISOString().split("T")[0]);
        } else {
            setComponents(defaultPackagingComponentRows());
        }
        setSelectedBoxPresetId("");
        setSelectedInstructionPresetId("");
        setInstructionSteps([]);
    };

    /** Copy template rows into the table; keep the preset selected so the user sees which template they started from. Saves send `components` for the chosen product group only — global presets are not updated. */
    const applyBoxPreset = (presetId) => {
        if (!presetId) {
            setSelectedBoxPresetId("");
            return;
        }
        setSelectedBoxPresetId(presetId);
        const preset = boxPresets.find((p) => String(p.id) === String(presetId));
        if (preset && Array.isArray(preset.components)) {
            setComponents(preset.components.map((c) => mapBoxPresetLine(c)));
        }
    };

    const addEmptyComponent = () => {
        setComponents((prev) => [...prev, newComponentRow()]);
    };

    const updateComponent = (idx, key, value) => {
        setComponents((prev) =>
            prev.map((c, i) => {
                if (i !== idx) return c;
                return { ...c, [key]: value };
            })
        );
    };

    const removeComponent = (idx) => {
        setComponents((prev) => prev.filter((_, i) => i !== idx));
    };

    const totalCost = components.reduce(
        (s, c) => s + Number(c.qty || 0) * Number(c.unitCost || 0),
        0
    );

    const handleSave = async () => {
        const name = String(productName || "").trim();
        if (!name || groupId == null) {
            toast.error("Select a product group", { position: "bottom-right" });
            return;
        }
        const normalized = components
            .filter((c) => String(c.componentName || "").trim())
            .map((c, index) => {
                const qty = Number(c.qty || 0);
                const unitCost = Number(c.unitCost || 0);
                const extendedCost = qty * unitCost;
                const vendorLabel =
                    (vendorOptions || []).find((v) => String(v.value) === String(c.vendorId || "").trim())
                        ?.label || "";
                return {
                    id: c.id || `packaging-component-${Date.now()}-${index}`,
                    componentName: String(c.componentName || "").trim(),
                    description: String(c.description || "").trim(),
                    supplier: vendorLabel || String(c.supplier || "").trim(),
                    vendorId: c.vendorId ? String(c.vendorId).trim() : "",
                    boxPresetId: c.boxPresetId ? String(c.boxPresetId).trim() : "",
                    qty,
                    unitCost,
                    extendedCost,
                };
            });
        if (normalized.length === 0) {
            toast.error("Add at least one component", { position: "bottom-right" });
            return;
        }
        const instructions = instructionSteps.map((s) => s.trim()).filter(Boolean);
        const existingByProduct = packagingList.find(
            (p) =>
                (p.groupId != null && p.groupId === groupId) ||
                p.productName.trim().toLowerCase() === name.toLowerCase()
        );
        const id =
            initialValue?.id ||
            existingByProduct?.id ||
            `packaging-${String(groupId)}`;
        const procurementPath =
            existingByProduct?.procurementPath != null ? existingByProduct.procurementPath : "";

        const payload = {
            id: existingByProduct?.id || id,
            groupId,
            productName: name,
            status: initialValue?.status || existingByProduct?.status || "Active",
            effectiveDate: String(effectiveDate || "").trim(),
            procurementPath: String(procurementPath || "").trim(),
            instructions,
            instructionCount: instructions.length,
            componentCount: normalized.length,
            totalCost: Math.round(totalCost * 100) / 100,
            components: normalized,
        };

        setSaving(true);
        try {
            await dispatch(
                savePackagingBom({
                    group_id: groupId,
                    effective_date: effectiveDate || null,
                    components: normalized.map((c) => ({
                        id: c.boxPresetId || null,
                        boxPresetId: c.boxPresetId || null,
                        box_preset_id: c.boxPresetId || null,
                        component: c.componentName,
                        description: c.description || "",
                        qty: c.qty,
                        unitCost: c.unitCost,
                        vendor_id: c.vendorId || null,
                    })),
                })
            ).unwrap();
            onCommit(payload, { isEdit, duplicateId: existingByProduct?.id });
            await dispatch(fetchPackagingBomUi()).unwrap();
            toast.success(
                existingByProduct && !isEdit
                    ? `Packaging BOM for "${name}" updated`
                    : isEdit
                      ? `Packaging BOM for "${name}" updated`
                      : `Packaging BOM for "${name}" created`,
                { position: "bottom-right" }
            );
            onClose();
        } catch (error) {
            const message =
                typeof error === "string"
                    ? error
                    : error?.message || "Failed to save packaging BOM";
            toast.error(message, { position: "bottom-right" });
        } finally {
            setSaving(false);
        }
    };

    const footer = (
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center justify-between gap-3">
                <div className="text-sm">
                    <span className="text-gray-400">Packaging Total</span>
                    <span className="ml-2 font-semibold tabular-nums text-gray-900">${totalCost.toFixed(2)}</span>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex h-7 items-center rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className={`inline-flex h-7 items-center justify-center rounded-md bg-[#111] px-3 text-sm font-medium text-white hover:bg-[#333] ${saving ? "opacity-60" : ""}`}
                    >
                        {saving ? (
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50">
                                <LoaderSpinner width="20" height="20" border="2" colorSpinner="white" />
                            </div>
                        ) : null}
                        Save Packaging BOM
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <Drawer isOpen={isOpen} onClose={onClose} width="650px" footer={footer}>
            <div className="flex h-full max-h-[100vh] flex-col overflow-hidden">
                <div className="border-b border-gray-200 px-6 py-4">
                    <div className="mb-1 flex items-center gap-2 text-xs text-gray-400">
                        <span>BOM</span>
                        <span>/</span>
                        <span>Packaging</span>
                    </div>
                    <h2 className="text-sm font-semibold text-gray-900">
                        {isEdit ? "Edit Product Packaging BOM" : "Add Product Packaging BOM"}
                    </h2>
                    <p className="mt-1 text-xs text-gray-400">
                        Select a product and apply a box preset — components auto-fill. All SKUs with matching names
                        inherit this packaging.
                    </p>
                </div>

                <div className="flex-1 space-y-5 overflow-y-auto px-6 py-4">
                    {/* Product name */}
                    <div className="space-y-1">
                        <label className={labelSm}>Product Name *</label>
                        {isEdit ? (
                            <input
                                value={productName}
                                disabled
                                className={`${inputSm} opacity-80`}
                            />
                        ) : (
                            <div className="relative">
                                <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                                <input
                                    ref={productInputRef}
                                    value={productSearch}
                                    onChange={(e) => {
                                        setProductSearch(e.target.value);
                                        setProductName("");
                                        setGroupId(null);
                                        setShowProductDropdown(true);
                                    }}
                                    onFocus={() => setShowProductDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowProductDropdown(false), 150)}
                                    placeholder="Search product groups…"
                                    className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-8 text-sm text-gray-900 outline-none focus:border-gray-400"
                                />
                                {productSearch ? (
                                    <button
                                        type="button"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => {
                                            setProductSearch("");
                                            setProductName("");
                                            setGroupId(null);
                                            setGroupResults([]);
                                            productInputRef.current?.focus();
                                        }}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        <XMarkIcon className="h-3 w-3" />
                                    </button>
                                ) : null}
                                {showProductDropdown &&
                                (groupSearchLoading || groupResults.length > 0 || productSearch.trim()) ? (
                                    <div className="absolute left-0 right-0 top-full z-50 mt-0.5 max-h-52 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                                        {groupSearchLoading ? (
                                            <div className="flex items-center justify-center gap-2 px-3 py-3 text-xs text-gray-400">
                                                <LoaderSpinner width="20" height="20" border="2" colorSpinner="black" />
                                                Searching…
                                            </div>
                                        ) : allSearchResultsTaken ? (
                                            <div className="px-3 py-2 text-xs text-gray-500">
                                                All matching product groups already have a packaging BOM in the table.
                                            </div>
                                        ) : visibleGroupResults.length > 0 ? (
                                            visibleGroupResults.map((row) => (
                                                <button
                                                    key={row.id}
                                                    type="button"
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => selectProductGroup(row)}
                                                    className={`w-full px-3 py-1.5 text-left text-sm transition-colors hover:bg-gray-50 ${
                                                        row.id === groupId
                                                            ? "bg-gray-100 font-medium text-gray-900"
                                                            : "text-gray-700"
                                                    }`}
                                                >
                                                    {row.name}
                                                </button>
                                            ))
                                        ) : productSearch.trim() ? (
                                            <div className="px-3 py-2 text-xs text-gray-400">
                                                No groups match &quot;{productSearch}&quot;
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        )}
                        {productName ? (
                            <p className="flex items-center gap-1 text-xs text-green-600">
                                <CheckIcon className="h-3 w-3" />
                                {productName} — all matching SKUs will inherit this packaging BOM
                            </p>
                        ) : (
                            <p className="text-xs text-gray-400">
                                All SKUs with this product name will inherit this packaging BOM.
                            </p>
                        )}
                    </div>

                    <div className="space-y-1">
                        <label className={labelSm}>Effective Date</label>
                        <DatePickerInput
                            value={effectiveDate}
                            onChange={setEffectiveDate}
                            placeholder="Effective date"
                            min={MIN_EFFECTIVE_DATE}
                            max={`${new Date().getFullYear() + 5}-12-31`}
                            className="w-full"
                            buttonClassName="!h-8 !border-gray-200 !bg-white !px-2.5 !text-sm"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className={labelSm}>Apply Instruction Preset</label>
                        <AddDrawerSelect
                            value={selectedInstructionPresetId || ""}
                            onChange={(v) => {
                                setSelectedInstructionPresetId(v);
                                if (!v) {
                                    setInstructionSteps([]);
                                    return;
                                }
                                const preset = instructionPresets.find((p) => String(p.id) === String(v));
                                if (preset && Array.isArray(preset.steps)) {
                                    setInstructionSteps([...preset.steps]);
                                } else {
                                    setInstructionSteps([]);
                                }
                            }}
                            options={instructionPresetSelectOptions}
                            emptyLabel="Select an instruction preset…"
                            showAllNone
                            allLabel="Select an instruction preset…"
                            renderInPortal
                            stackKey={PACKAGING_DRAWER_STACK.instructionPreset}
                            openStackKey={openDrawerSelectKey}
                            onStackKeyChange={setOpenDrawerSelectKey}
                            selectClassname="w-full"
                            buttonSelectedClassname="!py-1.5 !text-sm min-h-9"
                            renderOption={(option) => {
                                const preset = instructionPresets.find((p) => String(p.id) === option.value);
                                const n = preset && Array.isArray(preset.steps) ? preset.steps.length : 0;
                                return (
                                    <div className="flex min-w-0 flex-col gap-0.5 text-left">
                                        <span className="truncate text-gray-900">
                                            {preset?.name || option.label}
                                        </span>
                                        <span className="truncate text-xs text-gray-400">
                                            {n} instruction step{n !== 1 ? "s" : ""}
                                        </span>
                                    </div>
                                );
                            }}
                        />
                        {selectedInstructionPresetId ? (
                            <p className="flex items-center gap-1 text-xs text-green-600">
                                <CheckIcon className="h-3 w-3" />
                                {instructionSteps.length} step{instructionSteps.length !== 1 ? "s" : ""} loaded — saved
                                with this packaging BOM
                            </p>
                        ) : null}
                    </div>

                    <div className="space-y-2">
                        <label className={labelSm}>Apply Box Preset</label>
                        <AddDrawerSelect
                            value={selectedBoxPresetId || ""}
                            onChange={(v) => {
                                if (v) applyBoxPreset(v);
                                else setSelectedBoxPresetId("");
                            }}
                            options={boxPresetSelectOptions}
                            emptyLabel="Select a box preset…"
                            showAllNone
                            allLabel="Select a box preset…"
                            renderInPortal
                            stackKey={PACKAGING_DRAWER_STACK.boxPreset}
                            openStackKey={openDrawerSelectKey}
                            onStackKeyChange={setOpenDrawerSelectKey}
                            selectClassname="w-full"
                            buttonSelectedClassname="!py-1.5 !text-sm min-h-9"
                            renderOption={(option) => {
                                const p = boxPresets.find((x) => String(x.id) === option.value);
                                if (!p) {
                                    return <span className="text-gray-900">{option.label}</span>;
                                }
                                return (
                                    <div className="flex min-w-0 flex-col gap-0.5 text-left">
                                        <span className="truncate text-gray-900">
                                            {p.name || p.id}
                                            {p.totalCost != null ? (
                                                <span className="font-medium text-gray-700">
                                                    {" "}
                                                    — ${Number(p.totalCost).toFixed(2)}
                                                </span>
                                            ) : null}
                                        </span>
                                        <span className="truncate text-xs leading-tight text-gray-400">
                                            {formatBoxPresetPackagingSummary(p)}
                                        </span>
                                    </div>
                                );
                            }}
                        />
                        {selectedPreset ? (
                            <p className="text-xs text-gray-500">
                                Template: {selectedPreset.components?.length ?? 0} lines
                                {selectedPreset.totalCost != null
                                    ? ` · $${Number(selectedPreset.totalCost).toFixed(2)}`
                                    : ""}
                                . Saving writes this BOM for the selected product group only; box presets in settings are not changed.
                            </p>
                        ) : null}
                    </div>

                    {/* Components */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <label className={labelSm}>Components</label>
                            <button
                                type="button"
                                onClick={addEmptyComponent}
                                className="inline-flex h-6 items-center gap-1 rounded px-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                            >
                                <PlusIcon className="h-3 w-3" />
                                Add
                            </button>
                        </div>
                        {components.length > 0 ? (
                            <div className="overflow-hidden rounded-md border border-gray-200">
                                <table className="w-full text-sm">
                                    <thead className="border-b border-gray-200 bg-gray-50">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                                Component
                                            </th>
                                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                                Description
                                            </th>
                                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400 min-w-[170px] max-w-[170px]">
                                                Supplier
                                            </th>
                                            <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-400 min-w-[50px] max-w-[50px]">
                                                Qty
                                            </th>
                                            <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-400 min-w-[77px] max-w-[77px]">
                                                Unit Cost
                                            </th>
                                            <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                                Ext.
                                            </th>
                                            <th className="px-2 py-2" />
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 bg-white">
                                        {components.map((c, i) => {
                                            const ext =
                                                Number(c.qty || 0) * Number(c.unitCost || 0);
                                            return (
                                                <tr key={c.id}>
                                                    <td className="px-2 py-1.5">
                                                        <input
                                                            value={c.componentName}
                                                            onChange={(e) =>
                                                                updateComponent(i, "componentName", e.target.value)
                                                            }
                                                            placeholder="Box, Insert…"
                                                            className="w-full rounded border border-gray-200 px-1.5 py-0.5 text-sm"
                                                        />
                                                    </td>
                                                    <td className="px-2 py-1.5">
                                                        <input
                                                            value={c.description ?? ""}
                                                            onChange={(e) =>
                                                                updateComponent(i, "description", e.target.value)
                                                            }
                                                            placeholder=""
                                                            className="w-full min-w-[100px] rounded border border-gray-200 px-1.5 py-0.5 text-sm"
                                                        />
                                                    </td>
                                                    <td className="px-2 py-1.5 min-w-[160px] max-w-[160px]">
                                                        <AddDrawerSelect
                                                            options={vendorOptions}
                                                            value={c.vendorId || ""}
                                                            onChange={(v) =>
                                                                updateComponent(i, "vendorId", v || "")
                                                            }
                                                            noneLabel="Select supplier…"
                                                            emptyLabel="Select supplier…"
                                                            buttonSelectedClassname="!py-0.5 !text-sm"
                                                            selectClassname="w-full min-w-[160px] max-w-[160px]"
                                                            dropdownClassName="!gap-1"
                                                            renderInPortal={true}
                                                            stackKey={PACKAGING_DRAWER_STACK.supplier(i)}
                                                            openStackKey={openDrawerSelectKey}
                                                            onStackKeyChange={setOpenDrawerSelectKey}
                                                            isLoading={vendorSelectLoading}
                                                        />
                                                    </td>
                                                    <td className="px-2 py-1.5 min-w-[50px] max-w-[50px]">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={c.qty}
                                                            onChange={(e) =>
                                                                updateComponent(i, "qty", e.target.value)
                                                            }
                                                            className="w-full no-arrows-by-input rounded border border-gray-200 px-1 py-0.5 text-right text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                        />
                                                    </td>
                                                    <td className="px-2 py-1.5 min-w-[77px] max-w-[77px]">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            value={c.unitCost}
                                                            onChange={(e) =>
                                                                updateComponent(i, "unitCost", e.target.value)
                                                            }
                                                            className="w-full rounded no-arrows-by-input border border-gray-200 px-1 py-0.5 text-right text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                        />
                                                    </td>
                                                    <td className="px-3 py-1.5 text-right font-medium tabular-nums">
                                                        ${ext.toFixed(2)}
                                                    </td>
                                                    <td className="px-2 py-1.5">
                                                        <button
                                                            type="button"
                                                            onClick={() => removeComponent(i)}
                                                            className="text-gray-300 hover:text-red-400"
                                                        >
                                                            <XMarkIcon className="h-3.5 w-3.5" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="rounded-md border border-dashed border-gray-200 py-6 text-center text-sm text-gray-400">
                                Select a box preset above or click Add to build manually
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Drawer>
    );
}

function formatEffectiveDate(iso) {
    if (!iso || typeof iso !== "string") return "—";
    const parts = iso.split("T")[0].split("-");
    if (parts.length !== 3) return iso;
    const [y, m, d] = parts;
    return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`;
}

function formatLongDate(iso) {
    if (!iso) return "—";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "—";
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    }).format(date);
}

function ExpandedPackagingRow({ pkg }) {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState("components");
    const steps = Array.isArray(pkg.instructions) ? pkg.instructions : [];
    const [isEditingInstructions, setIsEditingInstructions] = useState(false);
    const [instructionDraft, setInstructionDraft] = useState(steps.length > 0 ? [...steps] : [""]);
    const [instructionSaving, setInstructionSaving] = useState(false);

    useEffect(() => {
        if (!isEditingInstructions) {
            setInstructionDraft(steps.length > 0 ? [...steps] : [""]);
        }
    }, [isEditingInstructions, pkg.id, pkg.instructions, steps]);

    const updateInstructionStep = (index, value) => {
        setInstructionDraft((prev) => prev.map((step, i) => (i === index ? value : step)));
    };

    const removeInstructionStep = (index) => {
        setInstructionDraft((prev) => {
            const next = prev.filter((_, i) => i !== index);
            return next.length > 0 ? next : [""];
        });
    };

    const handleSaveInstructions = async () => {
        const filteredSteps = instructionDraft.map((step) => String(step || "").trim()).filter(Boolean);
        try {
            setInstructionSaving(true);
            await dispatch(
                savePackagingInstructions({
                    group_id: pkg.groupId,
                    steps: filteredSteps,
                })
            ).unwrap();
            await dispatch(fetchPackagingBomUi()).unwrap();
            toast.success(`Instructions for "${pkg.productName}" updated`, { position: "bottom-right" });
            setIsEditingInstructions(false);
        } catch (error) {
            const message =
                typeof error === "string"
                    ? error
                    : error?.message || "Failed to save packaging instructions";
            toast.error(message, { position: "bottom-right" });
        } finally {
            setInstructionSaving(false);
        }
    };

    return (
        <div className="mx-4 my-2 ml-12 overflow-hidden rounded-md border border-gray-200">
            <div className="flex border-b border-gray-200 bg-gray-50">
                <button
                    type="button"
                    onClick={() => setActiveTab("components")}
                    className={`flex items-center gap-1.5 border-b-2 px-3 py-1.5 text-sm font-medium transition-colors ${
                        activeTab === "components"
                            ? "border-gray-900 text-gray-900"
                            : "border-transparent text-gray-400 hover:text-gray-600"
                    }`}
                >
                    <CubeIcon className="h-3 w-3" />
                    Components
                    <span className="ml-1 text-xs text-gray-400">({pkg.componentCount ?? 0})</span>
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab("instructions")}
                    className={`flex items-center gap-1.5 border-b-2 px-3 py-1.5 text-sm font-medium transition-colors ${
                        activeTab === "instructions"
                            ? "border-gray-900 text-gray-900"
                            : "border-transparent text-gray-400 hover:text-gray-600"
                    }`}
                >
                    <ClipboardDocumentListIcon className="h-3 w-3" />
                    Instructions
                    {steps.length > 0 ? (
                        <span className="ml-1 text-xs text-gray-400">({steps.length})</span>
                    ) : null}
                </button>
            </div>
            {activeTab === "components" ? (
                <table className="w-full text-sm">
                    <thead className="border-b border-gray-200 bg-gray-100">
                        <tr>
                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                Component
                            </th>
                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                Description
                            </th>
                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                Supplier
                            </th>
                            <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                Qty
                            </th>
                            <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                Unit Cost
                            </th>
                            <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                Ext. Cost
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                        {(pkg.components || []).map((c, i) => (
                            <tr key={c.id || i}>
                                <td className="px-3 py-2 font-medium text-gray-800">{c.componentName || "—"}</td>
                                <td className="max-w-[160px] truncate px-3 py-2 text-gray-500">
                                    {c.description || "—"}
                                </td>
                                <td className="max-w-[180px] truncate px-3 py-2 text-gray-500">{c.supplier || "—"}</td>
                                <td className="px-3 py-2 text-right">{c.qty}</td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                    ${Number(c.unitCost || 0).toFixed(2)}
                                </td>
                                <td className="px-3 py-2 text-right font-medium tabular-nums">
                                    ${Number(c.extendedCost ?? 0).toFixed(2)}
                                </td>
                            </tr>
                        ))}
                        <tr className="border-t border-gray-200 bg-gray-50 font-semibold">
                            <td colSpan={5} className="px-3 py-2 text-right text-xs uppercase tracking-wide text-gray-400">
                                Packaging Total
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                                ${Number(pkg.totalCost || 0).toFixed(2)}
                            </td>
                        </tr>
                    </tbody>
                </table>
            ) : (
                <div className="bg-white p-3">
                    {isEditingInstructions ? (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                    Steps
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setInstructionDraft((prev) => [...prev, ""])}
                                    className="text-sm text-gray-500 transition hover:text-gray-900"
                                >
                                    + Add step
                                </button>
                            </div>
                            <div className="space-y-2">
                                {instructionDraft.map((step, index) => (
                                    <div key={index} className="flex items-start gap-2">
                                        <span className="mt-2 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
                                            {index + 1}
                                        </span>
                                        <textarea
                                            rows={2}
                                            value={step}
                                            onChange={(e) => updateInstructionStep(index, e.target.value)}
                                            className="min-h-[44px] flex-1 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none transition focus:border-gray-400"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeInstructionStep(index)}
                                            className="mt-2 text-gray-300 transition hover:text-red-400"
                                        >
                                            <XMarkIcon className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <div className="flex items-center justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setInstructionDraft(steps.length > 0 ? [...steps] : [""]);
                                        setIsEditingInstructions(false);
                                    }}
                                    disabled={instructionSaving}
                                    className="inline-flex h-7 items-center rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveInstructions}
                                    disabled={instructionSaving}
                                    className="inline-flex relative h-7 items-center rounded-md bg-[#111] px-3 text-sm font-medium text-white hover:bg-[#333] disabled:opacity-60"
                                >
                                    {instructionSaving && (
                                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50">
                                            <LoaderSpinner width="16" height="16" border="2" colorSpinner="white" />
                                        </div>
                                    )}
                                    <span>Save</span>
                                </button>
                            </div>
                        </div>
                    ) : steps.length > 0 ? (
                        <ol className="space-y-2 border-b border-gray-200 pb-2 my-1">
                            {steps.map((step, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-900 text-[9px] font-bold text-white">
                                        {i + 1}
                                    </span>
                                    <span className="leading-relaxed">{step}</span>
                                </li>
                            ))}
                        </ol>
                    ) : (
                        <div className="flex flex-col items-center gap-1.5 py-4 text-gray-300">
                            <ClipboardDocumentListIcon className="h-6 w-6 opacity-50" />
                            <p className="text-xs text-gray-400">No packaging instructions yet</p>
                        </div>
                    )}
                    {!isEditingInstructions ? (
                        <div className="mt-3 flex items-center justify-between">
                            <span className="text-xs text-gray-300">
                                Last updated {formatLongDate(pkg.instructionsUpdatedAt)}
                            </span>
                            <button
                                type="button"
                                onClick={() => setIsEditingInstructions(true)}
                                className="text-sm text-gray-400 transition hover:text-gray-700"
                            >
                                Edit Instructions
                            </button>
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
}

const PackagingBomPanel = ({ onMetricsChange }) => {
    const dispatch = useDispatch();
    const { filtersByTab, selectedIdsByTab, expandedIdsByTab, data, loadingKeys: bomLoadingKeys = [] } =
        useSelector(selectBomUi);
    const filters = filtersByTab.packaging;
    const selectedIds = selectedIdsByTab.packaging;
    const expandedIds = expandedIdsByTab.packaging;
    const items = data.packaging || [];
    const mergedInstructionPresetsForDrawer = useMemo(() => {
        const fromSettings = data.instructionPresets || [];
        const fromDb = data.packagingInstructionPresetOptions || [];
        return [...fromSettings, ...fromDb];
    }, [data.instructionPresets, data.packagingInstructionPresetOptions]);
    const mergedBoxPresetsForDrawer = useMemo(() => {
        const fromSettings = data.boxPresets || [];
        const fromDb = data.packagingBoxPresetOptions || [];
        return [...fromSettings, ...fromDb];
    }, [data.boxPresets, data.packagingBoxPresetOptions]);
    const { vendorsToSelect, loadingKeys: groupLoadingKeys } = useSelector((s) => s.productGroup);
    const [editingItem, setEditingItem] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
    const [rowDeleteTarget, setRowDeleteTarget] = useState(null);
    const [bulkQtyOpen, setBulkQtyOpen] = useState(false);
    const [bulkQtySearch, setBulkQtySearch] = useState("");
    const [bulkQtyComponentKey, setBulkQtyComponentKey] = useState("");
    const [bulkQtyValue, setBulkQtyValue] = useState("");
    const [bulkQtyApplying, setBulkQtyApplying] = useState(false);
    const bulkQtyRef = useRef(null);

    const deletePackagingBusy = bomLoadingKeys.includes(BomUiLoadingKeys.deletePackagingBom);

    const expandedSet = useMemo(() => new Set(expandedIds), [expandedIds]);
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const selectedRows = useMemo(
        () => items.filter((item) => selectedSet.has(item.id)),
        [items, selectedSet]
    );

    const vendorOptions = useMemo(
        () =>
            (vendorsToSelect || []).map((vendor) => ({
                value: String(vendor.value),
                label: vendor.label,
            })),
        [vendorsToSelect]
    );
    const vendorSelectLoading = groupLoadingKeys.includes(ProductGroupLoadingKeys.getSuppliers);

    const bulkQtyComparableComponents = useMemo(() => {
        if (selectedRows.length === 0) return [];
        const labels = new Map();
        for (const row of selectedRows) {
            for (const component of row.components || []) {
                const key = normalizePackagingComponentName(component.componentName);
                if (!key) continue;
                if (!labels.has(key)) {
                    labels.set(key, String(component.componentName || "").trim());
                }
            }
        }
        return Array.from(labels.entries())
            .map(([key, label]) => ({
                key,
                label: label || key,
            }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
    }, [selectedRows]);

    const filteredBulkQtyComponents = useMemo(() => {
        const query = normalizePackagingComponentName(bulkQtySearch);
        if (!query) return bulkQtyComparableComponents;
        return bulkQtyComparableComponents.filter((item) => item.key.includes(query));
    }, [bulkQtyComparableComponents, bulkQtySearch]);

    const selectedBulkQtyComponent = useMemo(
        () => bulkQtyComparableComponents.find((item) => item.key === bulkQtyComponentKey) || null,
        [bulkQtyComparableComponents, bulkQtyComponentKey]
    );

    useEffect(() => {
        dispatch(getSuppliers({}));
    }, [dispatch]);

    useEffect(() => {
        if (!bulkQtyOpen) return undefined;
        const handleOutside = (event) => {
            if (bulkQtyRef.current && !bulkQtyRef.current.contains(event.target)) {
                if (!bulkQtyApplying) {
                    setBulkQtyOpen(false);
                    setBulkQtyComponentKey("");
                    setBulkQtyValue("");
                    setBulkQtySearch("");
                }
            }
        };
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, [bulkQtyApplying, bulkQtyOpen]);

    const filteredRows = useMemo(() => {
        const search = String(filters.search || "").trim().toLowerCase();
        return items.filter((item) => {
            const matchesSearch =
                !search ||
                [item.productName, item.procurementPath, item.status]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase()
                    .includes(search);
            const matchesProductName = filters.productName === "all" || item.productName === filters.productName;
            return matchesSearch && matchesProductName;
        });
    }, [filters, items]);

    const productOptions = useMemo(
        () =>
            Array.from(new Set(items.map((item) => item.productName).filter(Boolean)))
                .sort((a, b) => a.localeCompare(b))
                .map((value) => ({
                    value,
                    label: value,
                })),
        [items]
    );

    const allSelected =
        filteredRows.length > 0 && filteredRows.every((row) => selectedSet.has(row.id));
    const someSelected = filteredRows.some((row) => selectedSet.has(row.id));
    const hasFilters = Boolean(filters.search) || filters.productName !== "all";

    useEffect(() => {
        if (!someSelected) {
            setBulkQtyOpen(false);
            setBulkQtyComponentKey("");
            setBulkQtyValue("");
            setBulkQtySearch("");
        }
    }, [someSelected]);

    const productNameFilterValue = filters.productName || "all";
    const productNameSelected =
        productNameFilterValue === "all" || productNameFilterValue === "" ? [] : [String(productNameFilterValue)];

    useEffect(() => {
        onMetricsChange?.({
            totalRows: filteredRows.length,
            selectedRows: selectedIds.length,
            totalCost: filteredRows.reduce((sum, row) => sum + Number(row.totalCost || 0), 0),
        });
    }, [filteredRows, onMetricsChange, selectedIds.length]);

    const toggleSelection = (id) => {
        dispatch(toggleBomRowSelection({ tab: "packaging", id }));
    };

    const handleMasterCheck = () => {
        dispatch(
            setBomRowSelection({
                tab: "packaging",
                ids: allSelected ? [] : filteredRows.map((row) => row.id),
            })
        );
    };

    const performBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        const rows = items.filter((item) => selectedIds.includes(item.id));
        const groupIds = [
            ...new Set(
                rows
                    .map((r) => r.groupId)
                    .filter((g) => g != null && Number.isFinite(Number(g)))
                    .map((g) => Number(g))
            ),
        ];
        try {
            if (groupIds.length > 0) {
                await dispatch(deletePackagingBom({ group_ids: groupIds })).unwrap();
            }
            dispatch(deleteBomPackaging(Array.from(selectedIds)));
            toast.success(
                `Deleted ${selectedIds.length} packaging BOM${selectedIds.length !== 1 ? "s" : ""}`,
                { position: "bottom-right" }
            );
            setBulkDeleteModalOpen(false);
            dispatch(fetchPackagingBomUi());
        } catch (error) {
            const message =
                typeof error === "string"
                    ? error
                    : error?.message || "Failed to delete packaging BOM";
            toast.error(message, { position: "bottom-right" });
        }
    };

    const performRowDelete = async (pkg) => {
        if (!pkg) return;
        try {
            if (pkg.groupId != null && Number.isFinite(Number(pkg.groupId))) {
                await dispatch(deletePackagingBom({ group_ids: [Number(pkg.groupId)] })).unwrap();
            }
            dispatch(deleteBomPackaging([pkg.id]));
            toast.success(`Packaging BOM for "${pkg.productName}" deleted`, {
                position: "bottom-right",
            });
            setRowDeleteTarget(null);
            dispatch(fetchPackagingBomUi());
        } catch (error) {
            const message =
                typeof error === "string"
                    ? error
                    : error?.message || "Failed to delete packaging BOM";
            toast.error(message, { position: "bottom-right" });
        }
    };

    const openAdd = () => {
        setEditingItem(null);
        setDrawerOpen(true);
    };

    const handleCommit = (payload, { isEdit }) => {
        if (isEdit) {
            dispatch(updateBomPackaging(payload));
        } else {
            const dup = items.find(
                (p) =>
                    (payload.groupId != null && p.groupId === payload.groupId) ||
                    p.productName.trim().toLowerCase() === payload.productName.trim().toLowerCase()
            );
            if (dup) {
                dispatch(updateBomPackaging({ ...dup, ...payload, id: dup.id }));
            } else {
                dispatch(addBomPackaging(payload));
            }
        }
    };

    const resetBulkQtyPopover = () => {
        setBulkQtyOpen(false);
        setBulkQtyComponentKey("");
        setBulkQtyValue("");
        setBulkQtySearch("");
    };

    const applyBulkQtyUpdate = async () => {
        if (!selectedBulkQtyComponent) {
            toast.error("Select a component", { position: "bottom-right" });
            return;
        }
        const nextQty = Number(bulkQtyValue);
        if (!Number.isFinite(nextQty) || nextQty < 0) {
            toast.error("Enter a valid quantity", { position: "bottom-right" });
            return;
        }
        const targetRows = selectedRows.filter(
            (row) =>
                row.groupId != null &&
                (row.components || []).some(
                    (component) =>
                        normalizePackagingComponentName(component.componentName) === selectedBulkQtyComponent.key
                )
        );
        if (targetRows.length === 0) {
            toast.error("No selected packaging BOMs contain that component", { position: "bottom-right" });
            return;
        }
        setBulkQtyApplying(true);
        try {
            await Promise.all(
                targetRows.map((row) =>
                    dispatch(
                        savePackagingBom({
                            group_id: row.groupId,
                            components: (row.components || []).map((component) => ({
                                id: component.boxPresetId || component.box_preset_id || null,
                                boxPresetId: component.boxPresetId || component.box_preset_id || null,
                                box_preset_id: component.boxPresetId || component.box_preset_id || null,
                                component: String(component.componentName || "").trim(),
                                description: String(component.description || "").trim(),
                                supplier: String(component.supplier || "").trim(),
                                vendorId: component.vendorId ? String(component.vendorId).trim() : "",
                                qty:
                                    normalizePackagingComponentName(component.componentName) ===
                                    selectedBulkQtyComponent.key
                                        ? nextQty
                                        : Number(component.qty || 0),
                                unitCost: Number(component.unitCost || 0),
                            })),
                        })
                    ).unwrap()
                )
            );
            await dispatch(fetchPackagingBomUi()).unwrap();
            await dispatch(fetchProductBomUi()).unwrap();
            toast.success(
                `Updated "${selectedBulkQtyComponent.label}" qty to ${nextQty} for ${targetRows.length} packaging BOM${targetRows.length !== 1 ? "s" : ""}`,
                { position: "bottom-right" }
            );
            resetBulkQtyPopover();
        } catch (error) {
            const message =
                typeof error === "string" ? error : error?.message || "Failed to update component quantity";
            toast.error(message, { position: "bottom-right" });
        } finally {
            setBulkQtyApplying(false);
        }
    };

    return (
        <div className="flex h-full min-h-0 flex-1 flex-col">
            {/* Toolbar */}
            <div className="flex flex-shrink-0 flex-wrap items-center gap-2 border-b border-gray-200 bg-white px-4 py-2.5">
                <div className="relative max-w-sm flex-1">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                    <input
                        type="search"
                        value={filters.search}
                        onChange={(e) => dispatch(setBomFilter({ tab: "packaging", key: "search", value: e.target.value }))}
                        placeholder="Search product name…"
                        className="h-[35px] w-full rounded-md border no-search-input-chrome border-gray-200 bg-white pl-8 pr-8 text-sm text-gray-700 outline-none placeholder:text-gray-400 focus:border-gray-400"
                    />
                    {filters.search ? (
                        <button
                            type="button"
                            onClick={() => dispatch(setBomFilter({ tab: "packaging", key: "search", value: "" }))}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                        >
                            <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                    ) : null}
                </div>
                <ProductCatalogSelectFilter
                    allLabel="All Products"
                    selectClassname="w-[180px] flex-shrink-0"
                    buttonSelectedClassname={amberWhenFilterActive(productNameSelected.length > 0)}
                    options={productOptions}
                    selectedOptions={productNameSelected}
                    onChange={(next) =>
                        dispatch(
                            setBomFilter({
                                tab: "packaging",
                                key: "productName",
                                value: next.length === 0 ? "all" : next[0],
                            })
                        )
                    }
                    renderOption={(option) => <span className="text-gray-900">{option.label}</span>}
                />
                {hasFilters ? (
                    <button
                        type="button"
                        onClick={() => dispatch(clearBomFilters("packaging"))}
                        className="flex h-8 items-center gap-1 px-2 text-sm text-gray-500 hover:text-gray-900"
                    >
                        <XMarkIcon className="h-3 w-3" />
                        Clear
                    </button>
                ) : null}
                <div className="flex-1" />
                <button
                    type="button"
                    onClick={openAdd}
                    className="inline-flex h-8 items-center gap-1 rounded-md bg-[#111] px-3 text-sm font-medium text-white hover:bg-[#333]"
                >
                    <PlusIcon className="h-3.5 w-3.5" />
                    Add Packaging BOM
                </button>
            </div>

            {/* Bulk bar */}
            {someSelected ? (
                <div className="flex h-10 flex-shrink-0 animate-in items-center gap-3 border-b border-[#222] bg-[#111] px-4 text-white slide-in-from-top-1 duration-150">
                    <Square2StackIcon className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-sm font-medium">{selectedIds.length} selected</span>
                    <div className="mx-1 h-4 w-px bg-[#333]" />
                    <button
                        type="button"
                        onClick={() => {
                            const selectedGroupIds = items
                                .filter((item) => selectedIds.includes(item.id) && item.groupId != null)
                                .map((item) => item.groupId);
                            dispatch(
                                setBomSettingsOpen({
                                    open: true,
                                    section: "instructions",
                                    expandedInstructionGroupIds: selectedGroupIds,
                                })
                            );
                        }}
                        className="flex items-center gap-1.5 text-sm text-gray-200 transition-colors hover:text-white"
                    >
                        <ClipboardDocumentListIcon className="h-3.5 w-3.5" />
                        Edit Instructions
                    </button>
                    <div className="h-4 w-px bg-[#333]" />
                    <button
                        type="button"
                        onClick={() => {
                            const selectedGroupIds = items
                                .filter((item) => selectedIds.includes(item.id) && item.groupId != null)
                                .map((item) => item.groupId);
                            dispatch(
                                setBomSettingsOpen({
                                    open: true,
                                    section: "presets",
                                    expandedBoxGroupIds: selectedGroupIds,
                                })
                            );
                        }}
                        className="flex items-center gap-1.5 text-sm text-gray-200 transition-colors hover:text-white"
                    >
                        <CubeIcon className="h-3.5 w-3.5" />
                        Edit Box BOM
                    </button>
                    <div className="h-4 w-px bg-[#333]" />
                    <div className="relative" ref={bulkQtyRef}>
                        <button
                            type="button"
                            onClick={() => {
                                if (bulkQtyOpen) {
                                    resetBulkQtyPopover();
                                    return;
                                }
                                setBulkQtyOpen(true);
                                setBulkQtySearch("");
                                setBulkQtyComponentKey("");
                                setBulkQtyValue("");
                            }}
                            className="flex items-center gap-1.5 text-sm text-gray-200 transition-colors hover:text-white"
                        >
                            <span className="font-semibold">#</span>
                            Edit QTY
                            <ChevronDownIcon className="h-3 w-3" />
                        </button>
                        {bulkQtyOpen ? (
                            <div className="absolute left-0 top-full z-50 mt-2 w-[320px] rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
                                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                    Edit Component Quantity
                                </p>
                                <div className="relative">
                                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                                    <input
                                        value={bulkQtySearch}
                                        onChange={(e) => {
                                            setBulkQtySearch(e.target.value);
                                            if (selectedBulkQtyComponent) {
                                                setBulkQtyComponentKey("");
                                                setBulkQtyValue("");
                                            }
                                        }}
                                        placeholder="Search component..."
                                        className="h-9 w-full rounded-md border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-900 outline-none transition focus:border-gray-400"
                                    />
                                </div>
                                {!selectedBulkQtyComponent ? (
                                    <div className="mt-3 max-h-40 overflow-y-auto">
                                        {filteredBulkQtyComponents.length > 0 ? (
                                            filteredBulkQtyComponents.map((component) => (
                                                <button
                                                    key={component.key}
                                                    type="button"
                                                    onClick={() => {
                                                        setBulkQtyComponentKey(component.key);
                                                        setBulkQtySearch(component.label);
                                                    }}
                                                    className="w-full rounded-md px-3 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-50"
                                                >
                                                    {component.label}
                                                </button>
                                            ))
                                        ) : (
                                            <div className="px-3 py-2 text-xs text-gray-400">
                                                No shared components found for the selected groups.
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <div className="mt-3 rounded-md bg-[#111827] px-3 py-2 text-sm font-medium text-white">
                                            {selectedBulkQtyComponent.label}
                                        </div>
                                        <div className="mt-4 space-y-2">
                                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                                New Quantity For "{selectedBulkQtyComponent.label}"
                                            </p>
                                            <input
                                                type="number"
                                                min="0"
                                                value={bulkQtyValue}
                                                onChange={(e) => setBulkQtyValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "." || e.key === ",") {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                placeholder="e.g. 2"
                                                className="h-10 w-full no-arrows-by-input rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none transition focus:border-gray-400"
                                            />
                                        </div>
                                        <div className="mt-4 flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={resetBulkQtyPopover}
                                                disabled={bulkQtyApplying}
                                                className="inline-flex h-9 flex-1 items-center justify-center rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={applyBulkQtyUpdate}
                                                disabled={bulkQtyApplying}
                                                className="inline-flex h-9 flex-1 items-center justify-center rounded-md bg-[#111827] px-3 text-sm font-medium text-white transition hover:bg-[#1f2937] disabled:cursor-not-allowed disabled:opacity-60"
                                            >
                                                {bulkQtyApplying ? "Applying..." : "Apply"}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : null}
                    </div>
                    <div className="h-4 w-px bg-[#333]" />
                    <button
                        type="button"
                        onClick={() => setBulkDeleteModalOpen(true)}
                        className="flex items-center gap-1.5 text-sm text-red-400 transition-colors hover:text-red-300"
                    >
                        <TrashIcon className="h-3.5 w-3.5" />
                        Delete
                    </button>
                    <div className="flex-1" />
                    <button
                        type="button"
                        onClick={() => dispatch(setBomRowSelection({ tab: "packaging", ids: [] }))}
                        className="text-sm text-gray-400 transition-colors hover:text-white"
                    >
                        Clear
                    </button>
                </div>
            ) : null}

            {/* Table */}
            <div className="min-h-0 flex-1 overflow-auto bg-white">
                {filteredRows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-2 py-16 text-gray-300">
                        <CubeIcon className="h-8 w-8 opacity-40" />
                        <p className="text-sm text-gray-400">No packaging BOMs found</p>
                        <button
                            type="button"
                            onClick={openAdd}
                            className="mt-2 inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                            <PlusIcon className="h-3.5 w-3.5" />
                            Add Packaging BOM
                        </button>
                    </div>
                ) : (
                    <table className="w-full border-collapse text-left">
                        <thead className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50">
                            <tr className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                <th className="w-8 px-3 py-2.5">
                                    <button
                                        type="button"
                                        onClick={handleMasterCheck}
                                        className={`flex h-4 w-4 items-center justify-center rounded border transition-colors ${
                                            allSelected
                                                ? "border-[#111] bg-[#111]"
                                                : someSelected
                                                  ? "border-[#555] bg-[#555]"
                                                  : "border-gray-300 hover:border-gray-500"
                                        }`}
                                        aria-label="Select all"
                                    >
                                        {(allSelected || someSelected) && (
                                            <CheckIcon className="h-2.5 w-2.5 text-white" />
                                        )}
                                    </button>
                                </th>
                                <th className="w-6 px-1 py-2.5" />
                                <th className="px-4 py-2.5">Product Name</th>
                                <th className="px-4 py-2.5 text-right">Components</th>
                                <th className="px-4 py-2.5 text-right">Packaging Total</th>
                                <th className="px-4 py-2.5">Effective Date</th>
                                <th className="px-4 py-2.5">Procurement Path</th>
                                <th className="w-8 px-4 py-2.5" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredRows.map((pkg) => {
                                const isExpanded = expandedSet.has(pkg.id);
                                const isSelected = selectedSet.has(pkg.id);
                                return (
                                    <Fragment key={pkg.id}>
                                        <tr
                                            className={`group cursor-pointer transition-colors ${
                                                isSelected ? "bg-gray-50" : "bg-white hover:bg-gray-50/60"
                                            }`}
                                        >
                                            <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleSelection(pkg.id)}
                                                    className={`flex h-4 w-4 items-center justify-center rounded border transition-colors ${
                                                        isSelected
                                                            ? "border-[#111] bg-[#111]"
                                                            : "border-gray-300 hover:border-gray-500"
                                                    }`}
                                                    aria-label="Select row"
                                                >
                                                    {isSelected ? (
                                                        <CheckIcon className="h-2.5 w-2.5 text-white" />
                                                    ) : null}
                                                </button>
                                            </td>
                                            <td
                                                className="px-1 py-2.5 text-gray-400"
                                                onClick={() =>
                                                    dispatch(toggleBomExpandedRow({ tab: "packaging", id: pkg.id }))
                                                }
                                            >
                                                {isExpanded ? (
                                                    <ChevronDownIcon className="h-3.5 w-3.5" />
                                                ) : (
                                                    <ChevronRightIcon className="h-3.5 w-3.5" />
                                                )}
                                            </td>
                                            <td
                                                className="px-4 py-2.5"
                                                onClick={() =>
                                                    dispatch(toggleBomExpandedRow({ tab: "packaging", id: pkg.id }))
                                                }
                                            >
                                                <span className="text-sm font-medium text-gray-900">
                                                    {pkg.productName}
                                                </span>
                                            </td>
                                            <td
                                                className="px-4 py-2.5 text-right text-sm text-gray-400"
                                                onClick={() =>
                                                    dispatch(toggleBomExpandedRow({ tab: "packaging", id: pkg.id }))
                                                }
                                            >
                                                {pkg.componentCount ?? 0} items
                                            </td>
                                            <td
                                                className="px-4 py-2.5 text-right text-sm font-semibold tabular-nums text-gray-900"
                                                onClick={() =>
                                                    dispatch(toggleBomExpandedRow({ tab: "packaging", id: pkg.id }))
                                                }
                                            >
                                                ${Number(pkg.totalCost || 0).toFixed(2)}
                                            </td>
                                            <td
                                                className="px-4 py-2.5 text-sm text-gray-500"
                                                onClick={() =>
                                                    dispatch(toggleBomExpandedRow({ tab: "packaging", id: pkg.id }))
                                                }
                                            >
                                                {formatEffectiveDate(pkg.effectiveDate)}
                                            </td>
                                            <td
                                                className="px-4 py-2.5 text-sm text-gray-400"
                                                onClick={() =>
                                                    dispatch(toggleBomExpandedRow({ tab: "packaging", id: pkg.id }))
                                                }
                                            >
                                                {pkg.procurementPath || "—"}
                                            </td>
                                            <td className="px-4 py-2.5">
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setRowDeleteTarget(pkg);
                                                    }}
                                                    className="text-gray-300 opacity-0 transition-all hover:text-red-400 group-hover:opacity-100"
                                                >
                                                    <TrashIcon className="h-3.5 w-3.5" />
                                                </button>
                                            </td>
                                        </tr>
                                        {isExpanded ? (
                                            <tr className="border-b border-gray-100 bg-gray-50/60">
                                                <td colSpan={8} className="p-0">
                                                    <ExpandedPackagingRow pkg={pkg} />
                                                </td>
                                            </tr>
                                        ) : null}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <PackagingBomDrawer
                isOpen={drawerOpen}
                onClose={() => {
                    setDrawerOpen(false);
                    setEditingItem(null);
                }}
                initialValue={editingItem}
                packagingList={items}
                instructionPresets={mergedInstructionPresetsForDrawer}
                boxPresets={mergedBoxPresetsForDrawer}
                onCommit={handleCommit}
                vendorOptions={vendorOptions}
                vendorSelectLoading={vendorSelectLoading}
            />

            <ConfrimModal
                isOpen={bulkDeleteModalOpen}
                isLoading={deletePackagingBusy}
                title={`Delete ${selectedIds.length} packaging BOM${selectedIds.length !== 1 ? "s" : ""}? This cannot be undone.`}
                confirmButtonTitle="Delete"
                submitClick={performBulkDelete}
                cancelClick={() => {
                    if (!deletePackagingBusy) setBulkDeleteModalOpen(false);
                }}
                size="max-w-lg"
            />
            <ConfrimModal
                isOpen={Boolean(rowDeleteTarget)}
                isLoading={deletePackagingBusy}
                title={
                    rowDeleteTarget
                        ? `Delete packaging BOM for "${rowDeleteTarget.productName}"? This cannot be undone.`
                        : ""
                }
                confirmButtonTitle="Delete"
                submitClick={() => performRowDelete(rowDeleteTarget)}
                cancelClick={() => {
                    if (!deletePackagingBusy) setRowDeleteTarget(null);
                }}
                size="max-w-lg"
            />
        </div>
    );
};

export default PackagingBomPanel;
